import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const seoData = {
  '/': {
    title: 'Immersid - Live It, Build It | AR Interior Design Platform',
    description: 'Visualize, design, and build your dream space with AR technology. See your renovation before committing — transparent pricing, direct ordering, zero surprises.',
    keywords: 'interior design, augmented reality, home renovation, AR visualization, furniture, space planning',
  },
  '/about': {
    title: 'About Immersid | Our Mission & Story',
    description: 'Immersid combines AR, AI, and a direct marketplace to transform how homeowners design and renovate their spaces. Based in Hong Kong, serving Southeast Asia.',
    keywords: 'about immersid, interior design company, AR technology company, Hong Kong',
  },
  '/features': {
    title: 'Features | Immersid AR Interior Design Platform',
    description: 'AR room visualization, AI design suggestions, live pricing, direct ordering from verified suppliers, and project management — all in one platform.',
    keywords: 'AR features, room scanning, 3D visualization, live pricing, furniture marketplace',
  },
  '/how-it-works': {
    title: 'How It Works | Immersid',
    description: 'Scan your room, visualize designs in AR, get transparent pricing, and order everything from one platform. From idea to installation in weeks, not months.',
    keywords: 'how it works, AR design process, room scanning, interior design steps',
  },
  '/problems': {
    title: 'Problems We Solve | Immersid',
    description: 'Expensive mistakes, unclear pricing, unreliable contractors — we solve the biggest pain points in home renovation with technology and transparency.',
    keywords: 'renovation problems, interior design issues, transparent pricing, contractor coordination',
  },
  '/technology': {
    title: 'Technology | Immersid AR Platform',
    description: 'Built on ARKit, ARCore, and WebAR — our technology stack delivers real-time 3D rendering, accurate room mapping, and seamless AR experiences on any device.',
    keywords: 'AR technology, ARKit, ARCore, 3D rendering, interior design technology',
  },
  '/design-consultation': {
    title: 'Design Consultation | Immersid Interior Design Services',
    description: 'Expert interior design consultation — space planning, colour & material selection, furniture styling, lighting design, and full project management.',
    keywords: 'interior design consultation, space planning, furniture styling, design services',
  },
  '/blog': {
    title: 'Blog | Immersid — Interior Design & Technology Insights',
    description: 'Articles on interior design automation, industry trends, AR technology, and practical home renovation advice from the Immersid team.',
    keywords: 'interior design blog, AR technology articles, home renovation tips, design trends',
  },
  '/blog/interior-design-automation': {
    title: 'How Automation Is Reshaping Interior Design | Immersid Blog',
    description: 'Room scanning, AI layouts, automated pricing, and AR previews are transforming interior design. Here\'s what it means for homeowners.',
    keywords: 'interior design automation, AI design, AR visualization, automated pricing, room scanning',
  },
  '/blog/future-of-interior-design': {
    title: 'The Future of Interior Design: What the Next Decade Looks Like | Immersid Blog',
    description: 'Sustainability, invisible tech, AR as the standard tool, transparent pricing, and personalisation at scale — the forces reshaping interior design.',
    keywords: 'future interior design, design trends 2030, sustainable design, smart home, AR design tools',
  },
  '/faqs': {
    title: 'FAQs | Immersid',
    description: 'Frequently asked questions about Immersid, our AR platform, design consultation services, pricing, and how to get started.',
    keywords: 'FAQ, frequently asked questions, immersid help, AR platform questions',
  },
  '/careers': {
    title: 'Careers | Immersid',
    description: 'Join the Immersid team. We\'re building the future of interior design with AR, AI, and a passion for beautiful spaces.',
    keywords: 'careers, jobs, immersid careers, interior design jobs, AR technology jobs',
  },
  '/privacy-policy': {
    title: 'Privacy Policy | Immersid',
    description: 'Immersid\'s privacy policy — how we collect, use, and protect your personal data.',
  },
  '/terms-of-service': {
    title: 'Terms of Service | Immersid',
    description: 'Terms and conditions for using Immersid\'s website and services.',
  },
  '/cookie-policy': {
    title: 'Cookie Policy | Immersid',
    description: 'How Immersid uses cookies and similar technologies on our website.',
  },
  '/disclaimer': {
    title: 'Disclaimer | Immersid',
    description: 'Legal disclaimer for Immersid\'s website content and services.',
  },
};

const defaultSeo = {
  title: 'Immersid - Live It, Build It',
  description: 'Interior design technology combining AR, AI, and a direct marketplace to help homeowners visualize, design, and build their dream spaces.',
  keywords: 'interior design, augmented reality, home renovation, AR, immersid',
};

const SEO = () => {
  const location = useLocation();

  useEffect(() => {
    const data = seoData[location.pathname] || defaultSeo;

    document.title = data.title;

    const setMeta = (name, content) => {
      if (!content) return;
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    const setOg = (property, content) => {
      if (!content) return;
      let el = document.querySelector(`meta[property="${property}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('property', property);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', data.description);
    if (data.keywords) setMeta('keywords', data.keywords);

    setOg('og:title', data.title);
    setOg('og:description', data.description);
    setOg('og:type', 'website');
    setOg('og:site_name', 'Immersid');

  }, [location.pathname]);

  return null;
};

export default SEO;
